import { type FC, useCallback, useMemo } from "react";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { createAccountAddOnsFetcher } from "./Queries/queries";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import dateTimeUtils from "utils/dateTimeUtils";
import type { AddOnDetailRow } from "../types/addOns";

import styles from "./AddOnsReport.module.scss";

export interface Props {
  customerId: number;
  filter: PerformanceWithAccountFilter;
  hasDataForExport?: (state: ExportAvailability) => void;
}

export const AddOnsReportDrilldown: FC<Props> = ({ customerId, filter, hasDataForExport }) => {
  const fetchAddOnsPage = useMemo(() => createAccountAddOnsFetcher(customerId, filter), [customerId, filter]);
  const queryKeyBase = useMemo(() => ["accountAddOns", customerId, filter] as const, [customerId, filter]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) => {
      hasDataForExport?.({
        hasDataToExport,
      });
    },
    [hasDataForExport],
  );

  const columnOptions = useMemo<ColumnOption<AddOnDetailRow>[]>(
    () => [
      {
        name: "ADD-ON",
        width: 6,
        isSortable: true,
        sortKey: "AddonName",
        render: ({ AddonName }) => AddonName,
      },
      {
        name: "DATE ENABLED",
        width: 5,
        isSortable: true,
        sortKey: "DateEnabled",
        render: ({ DateEnabled }) => (DateEnabled ? dateTimeUtils.formatDate(DateEnabled) : "-"),
      },
      {
        name: "EXPIRATION DATE",
        width: 5,
        isSortable: true,
        sortKey: "ExpirationDate",
        render: ({ ExpirationDate }) => (ExpirationDate ? dateTimeUtils.formatDate(ExpirationDate) : "-"),
      },
    ],
    [],
  );

  return (
    <div className={styles.tableSection}>
      <GenericPerformanceListWithServerPagination
        title="Add-Ons"
        columns={columnOptions}
        filter={filter}
        fetchPage={fetchAddOnsPage}
        queryKeyBase={queryKeyBase}
        hasDataForExport={updateExportDataState}
        isDateAllTime
      />
    </div>
  );
};

export default AddOnsReportDrilldown;
